<template>
  <div class="row">
    <div class="style-row">
      <label>Choose AI Button Icon</label>
      <div>
        <Button @click="openDialog=true" style="margin-right: 10px;">Choose Icon</Button>
        <i :class="theme.aiButton.icon" />
      </div>

      </div>

    <div class="style-row">
      <label for="label">Button Position</label>
      <div class="box-container">
        <div class="box">
          <div class="corner top-left" :class="{ selected: aiButtonPosition === 'top-left' }" @click="aiButtonPosition = 'top-left'" />
          <div class="corner top-right" :class="{ selected: aiButtonPosition === 'top-right' }" @click="aiButtonPosition = 'top-right'" />
          <div class="corner bottom-left" :class="{ selected: aiButtonPosition === 'bottom-left' }" @click="aiButtonPosition = 'bottom-left'" />
          <div class="corner bottom-right" :class="{ selected: aiButtonPosition === 'bottom-right' }" @click="aiButtonPosition = 'bottom-right'" />
        </div>
      </div>
    </div>

    <div class="style-row">
      <label for="label">Icon Color Picker</label>
      <div style="width: 150px; display: inline-block">
        <ColorButton @click="colorPickerVisible.toggle($event)" :color="aiButtonColor" style="flex: 3"  />
        <OverlayPanel ref="colorPickerVisible">
          <ColorPicker v-model="aiButtonColor"
                       @edit-theme="$refs.colorPickerVisible.toggle(false); themeDialogVisible = true"
          />
        </OverlayPanel>
      </div>
    </div>

    <div class="style-row">
      <label for="label">Select Button Size</label>
      <SelectButton
          v-model="aiButtonSize"
          :options="buttonSizeOptions"
      />
    </div>

    <div class="style-row">
      <label for="label">Button Background Color</label>
      <div style="width: 150px; display: inline-block">
        <ColorButton @click="bgColorPickerVisible.toggle($event)" :color="aiButtonBgColor" style="flex: 3"  />
        <OverlayPanel ref="bgColorPickerVisible">
          <ColorPicker v-model="aiButtonBgColor"
                       @edit-theme="$refs.bgColorPickerVisible.toggle(false); themeDialogVisible = true"
          />
        </OverlayPanel>
      </div>
    </div>

    <div class="style-row">
      <label for="label">Button Outline Color</label>
      <div style="width: 150px; display: inline-block">
        <ColorButton @click="outlineColorPickerVisible.toggle($event)" :color="aiButtonOutlineColor" style="flex: 3"  />
        <OverlayPanel ref="outlineColorPickerVisible">
          <ColorPicker v-model="aiButtonOutlineColor"
                       @edit-theme="$refs.outlineColorPickerVisible.toggle(false); themeDialogVisible = true"
          />
        </OverlayPanel>
      </div>
    </div>

    <div class="style-row">
      <label for="label">Button Opacity</label>
      <Slider v-model="aiButtonOpacity" :step="0.1" :min="0" :max="1" style="width:150px;" />
    </div>

    <div class="style-row" style="border:none;">
      <label for="label">AI Button Tooltip</label>
      <Textarea style="margin-left:10px;" v-model="aiButtonTooltip" rows="2" cols="30" />
    </div>
  </div>

  <Dialog v-model:visible="openDialog" style="width: 50vw" header="Select Icon">
    <template #header>
      <div style="display:flex;justify-content:space-between;width:100%">
        <h4>Select Icon</h4>
        <InputText v-model="searchQuery" placeholder="Search icons" />
      </div>
    </template>

    <div class="icon-grid">
      <div v-for="icon in filteredIcons" :key="icon.name" class="icon-item" @click="selectIcon(icon)">
        <div class="icon-container">
          <i :class="icon.icon"></i>
        </div>
      </div>
    </div>
  </Dialog>
</template>
<script setup lang="ts">
import {computed, ref} from 'vue'
import {useMainStore, useSlidesStore} from "@/store";
import {storeToRefs} from "pinia";
import { PrimeIcons } from 'primevue/api';
import ColorButton from "@/views/Editor/Toolbar/common/ColorButton.vue";
import ColorPicker from "@/components/ColorPicker/index.vue";

const {themeDialogVisible} = storeToRefs(useMainStore())
const { theme } = storeToRefs(useSlidesStore())

const openDialog = ref(false)
const searchQuery = ref('')
const colorPickerVisible = ref()
const outlineColorPickerVisible = ref()
const bgColorPickerVisible = ref()
const buttonSizeOptions = ref(['Small', 'Medium', 'Large']);

const allIcons: string[] = Object.keys(PrimeIcons)
const filteredIcons = computed(() => {
  return allIcons
      .filter(icon => icon.toLowerCase().includes(searchQuery.value.toLowerCase()))
      .map(icon => ({ name: icon, icon: PrimeIcons[icon] }))
})

const selectIcon = (icon: { name: string; icon: string }) => {
  theme.value.aiButton.icon = icon.icon;
  openDialog.value = false;
};

const aiButtonTooltip = computed({
  get: () => theme.value.aiButton.tooltip ?? 'Ask AI',
  set: val => theme.value && (theme.value.aiButton.tooltip = val)
})

const aiButtonPosition = computed({
  get: () => theme.value.aiButton.position ?? 'top-right',
  set: val => theme.value && (theme.value.aiButton.position = val)
})

const aiButtonSize = computed({
  get: () => theme.value.aiButton.size ?? 'Medium',
  set: val => theme.value && (theme.value.aiButton.size = val)
})

const aiButtonColor = computed({
  get: () => theme.value.aiButton.color ?? 'transparent',
  set: val => theme.value && (theme.value.aiButton.color = val)
})

const aiButtonBgColor = computed({
  get: () => theme.value.aiButton.backgroundColor ?? 'transparent',
  set: val => theme.value && (theme.value.aiButton.backgroundColor = val)
})

const aiButtonOutlineColor = computed({
  get: () => theme.value.aiButton.outlineColor ?? 'transparent',
  set: val => theme.value && (theme.value.aiButton.outlineColor = val)
})

const aiButtonOpacity = computed({
  get: () => theme.value.aiButton.opacity ?? 1,
  set: val => theme.value && (theme.value.aiButton.opacity = val)
})
</script>

<style scoped lang="scss">
.style-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 16px;
  border-bottom: 1px solid #e0e0e0;
}

.style-row label {
  flex-shrink: 0;
  margin-right: 10px;
}

.style-row button {
  flex-shrink: 0;
}

:deep(.p-highlight span) { z-index: 100; }

:deep(.p-selectbutton .p-button::before) {
  transition: none !important;
}

:deep(.p-inputtext) {
  text-align: left !important;
}

.icon-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr); /* 4 columns */
  grid-gap: 10px;
  row-gap: 30px;
  margin-top: 20px;
  height: 500px;
}

.icon-item {
  text-align: center;
  cursor: pointer;
}

.icon-container {
  display:flex;
  justify-content: center;
  flex-direction: column;
}

.smaller-switch {
  transform: scale(0.6);
}

.box-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 5px;
}

.box {
  width: 100px;
  height: 100px;
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
}

.corner {
  width: 50px;
  height: 50px;
  border: none;
  background-color: rgba(0, 0, 0, 0.1);
  cursor: pointer;
  text-align: center;
  line-height: 50px;
  font-size: 12px;
  transition: background-color 0.1s ease;
}

.corner:hover {
  background-color: rgba(0, 0, 0, 0.2);
}

.top-left {
  grid-column: 1;
  grid-row: 1;
}

.top-right {
  grid-column: 2;
  grid-row: 1;
}

.bottom-left {
  grid-column: 1;
  grid-row: 2;
}

.bottom-right {
  grid-column: 2;
  grid-row: 2;
}

.selected {
  background-color: #2196F3;
}

</style>